import {CA} from '../../analytics/Analytics'
import {UIGameEndController} from './UIGameEndController'
import {UIGameManager} from '../UIGameManager'
import {AnalyticEventCategory, AnalyticEventType} from '../../../../../shared/entity_datas/EntityDatas'
import {ReplayInitiationData} from '@colonist/game-base-types'
import {UIReplayController} from '../../ui_replay/UIReplayController'
import {socket} from '../../EntryShared'
import {UIAPIEndpoints} from '../../network/UIAPIEndpoints'
import {UIAjaxWrapper} from '../../network/UIAjaxWrapper'

export class UIGameEndReturnToMapButtonController {
    private readonly uiGameManager: UIGameManager
    private readonly gameEndController: UIGameEndController

    private replayDataUnavailable: boolean = false
    private debounceTimer: NodeJS.Timeout | null = null

    constructor(uiGameManager: UIGameManager, gameEndController: UIGameEndController) {
        this.uiGameManager = uiGameManager
        this.gameEndController = gameEndController
    }

    async onClickButton() {
        // TODO remove debounce logic once ReactJS state is set up properly, UIGameEnd after UIGameReturnToStats in UIGameLayoutPortrait
        if(this.debounceTimer) {
            clearTimeout(this.debounceTimer)
        }

        this.debounceTimer = setTimeout(async() => {
            this.debounceTimer = null
            await this.handleButtonClick()
        }, 100)
    }

    private async handleButtonClick() {
        CA.sendGoogleAnalyticsEvent(CA.Category.EndGame, CA.Label.ClickedEndGameReturnToMap)

        void CA.sendAnalyticEventOnce({
            type: AnalyticEventType.EndGameReturnToMapButton,
            category: AnalyticEventCategory.Game,
        })

        const isReplay = this.uiGameManager.gameStore.getState().gameReplay.replayControls !== undefined
        if(this.replayDataUnavailable) {
            this.toggleBetweenStatsAndMap()
        } else if(isReplay) {
            this.toggleBetweenStatsAndReplay()
        } else {
            void this.initGameEndReplay()
        }
    }

    private toggleBetweenStatsAndMap() {
        const isStatsVisible = !this.gameEndController.isVisible

        this.gameEndController.setVisible(isStatsVisible)

        const isMapVisible = !isStatsVisible
        this.uiGameManager.mapController.setInteractionEnabled(isMapVisible)
        this.uiGameManager.gameStore.getState().gameEnd.setIsVisible(isStatsVisible)
    }

    private toggleBetweenStatsAndReplay() {
        const replayControls = this.uiGameManager.gameStore.getState().gameReplay.replayControls
        if(replayControls == undefined) return

        const isStatsVisible = this.gameEndController.isVisible
        const actionBeforeEndgameScreenIsShown = replayControls.numberOfActions - 2
        const actionIndexForEndgameScreen = replayControls.numberOfActions - 1
        const actionIndex = isStatsVisible ? actionBeforeEndgameScreenIsShown : actionIndexForEndgameScreen
        replayControls.onChangeActionIndex(actionIndex)
        this.uiGameManager.gameStore.getState().gameEnd.setIsVisible(!isStatsVisible)
    }

    private async initGameEndReplay() {
        this.toggleBetweenStatsAndMap()

        const replayData = await this.getReplayData()

        if(replayData == undefined) {
            this.replayDataUnavailable = true
            return
        }

        this.startReplay(replayData)
    }

    private async getReplayData(): Promise<ReplayInitiationData | undefined> {
        const isSpectator = this.uiGameManager.gameController.isPlayerSpectator
        const playerColor = isSpectator ? this.uiGameManager.gameController.playOrder[0] : this.uiGameManager.gameController.myColor
        const replayDataUrl = UIAPIEndpoints.apiReplayDataFromGameId(socket.gameListener.databaseGameId, playerColor)
        const displayError = false
        const replayDataResponse = await UIAjaxWrapper.getRequestHandler(replayDataUrl, undefined, undefined, displayError)

        if(replayDataResponse == undefined || !replayDataResponse.isSuccess) return undefined
        const replayData = replayDataResponse.json.data as ReplayInitiationData
        replayData.rankedUserStates = this.gameEndController.rankedUserStates

        return replayDataResponse.json.data
    }

    private startReplay(replayData: ReplayInitiationData) {
        this.uiGameManager.resetGameUIToRebuildOnReconnect()
        this.gameEndController.destroy()

        // Temporary fix for Discord, overwrite PlayerStates with the ones used in the game since these have the correct discord usernames
        const recentPlayerStates = this.uiGameManager.gameController.getPlayerValidators().map((playerValidator) => playerValidator.userState)
        replayData.playerUserStates = recentPlayerStates

        const isLiveGame = true
        const replayController = new UIReplayController(replayData, socket.actionController, isLiveGame)
        const actionIndexBeforeEndgame = replayController.actionIndexForEndgame - 1
        this.uiGameManager.gameStore.getState().gameReplay.setActionIndex(actionIndexBeforeEndgame)
    }
}
